import React, { useState } from "react";
import axios from "axios";

function FetchOrderStatus() {
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [orderNumbers, setOrderNumbers] = useState("");
    const [fetchCount, setFetchCount] = useState(0);
    const [expandedOrders, setExpandedOrders] = useState(new Set());

    const API_KEY = process.env.REACT_APP_SHIPSTATION_DRMTLGY_API_KEY || "";
    const API_SECRET = process.env.REACT_APP_SHIPSTATION_DRMTLGY_API_SECRET || "";

    const fetchOrderData = async (orderNumber) => {
        setLoading(true);
        if (!API_KEY || !API_SECRET) {
            alert("ShipStation DRMTLGY credentials are not configured.");
            setLoading(false);
            return null;
        }
        const authHeader = `Basic ${btoa(API_KEY + ":" + API_SECRET)}`;

        try {
            // Try different formats to see which one works
            const cleanOrderNumber = orderNumber.replace(/^#/, '').replace(/^0/, ''); // Remove leading # and 0

            // Test different formats
            const formatsToTry = [
                `0${cleanOrderNumber}`,           // Format 1: with leading 0
                `#0${cleanOrderNumber}`,          // Format 2: with # and 0
                cleanOrderNumber,                 // Format 3: just the number
                `#${cleanOrderNumber}`,           // Format 4: with # only
            ];

            let response = null;

            // Try each format until one works
            for (const format of formatsToTry) {
                try {
                    const encodedOrderNumber = encodeURIComponent(format);
                    console.log(`Trying order number format: ${format}`);

                    response = await axios.get(`https://ssapi.shipstation.com/orders?orderNumber=${encodedOrderNumber}`, {
                        headers: {
                            "Authorization": authHeader,
                        },
                        timeout: 10000, // Add timeout
                    });

                    // If we get a successful response with orders, break the loop
                    if (response.data && response.data.orders && response.data.orders.length > 0) {
                        console.log(`Success with format: ${format}`);
                        break;
                    }
                } catch (error) {
                    console.log(`Format ${format} failed:`, error.message);
                    continue;
                }
            }

            if (!response) {
                console.error("All order number formats failed");
                return null;
            }

            console.log("API Response:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching data:", error);
            if (error.response) {
                console.error("Response status:", error.response.status);
                console.error("Response data:", error.response.data);
            }
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleOrderNumbersChange = (e) => {
        const inputValue = e.target.value;

        // Process each line to add leading 0 if not already present
        const processedOrderNumbers = inputValue
            .split("\n")
            .map(order => {
                let cleanOrder = order.trim();
                // Remove any existing # and add 0 if not starting with 0
                cleanOrder = cleanOrder.replace(/^#/, '');
                if (cleanOrder && !cleanOrder.startsWith('0')) {
                    return `0${cleanOrder}`;
                }
                return cleanOrder;
            })
            .join("\n");

        setOrderNumbers(processedOrderNumbers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const orderNumbersArray = orderNumbers
            .split("\n")
            .map(order => order.trim())
            .filter(order => order);

        const fetchedOrders = [];

        for (let i = 0; i < orderNumbersArray.length; i++) {
            const orderNumber = orderNumbersArray[i];
            const orderData = await fetchOrderData(orderNumber);

            if (orderData && orderData.orders && orderData.orders.length > 0) {
                const order = orderData.orders[0];

                // Extract tracking number from the order data
                // const trackingNumber = order.trackingNumber ||
                //     (order.advancedOptions && order.advancedOptions.trackingNumber) ||
                //     "N/A";

                // Extract externally fulfilled information
                const externallyFulfilled = order.externallyFulfilled || false;
                // const servicedBy = order.serviceCode || "N/A";

                fetchedOrders.push({
                    orderNumber,
                    orderId: order.orderNumber || "N/A",
                    shipStationOrderNumber: order.orderId || "N/A",
                    orderStatus: order.orderStatus || "No Status",
                    carrierCode: order.carrierCode || "N/A",
                    externallyFulfilled: externallyFulfilled,
                    servicedBy: order.serviceCode || "N/A", // This is stored as servicedBy
                    tagIds: order.tagIds || [],
                    items: order.items || [],
                });
                setFetchCount((prevCount) => prevCount + 1);
            } else {
                fetchedOrders.push({
                    orderNumber,
                    orderId: "N/A",
                    shipStationOrderNumber: "N/A",
                    orderStatus: "Error fetching data",
                    orderCarrier: "N/A",
                    carrierCode: "Error fetching carrier",
                    externallyFulfilled: false,
                    servicedBy: "N/A", // This should be servicedBy, not serviceCode
                    tagIds: [],
                    items: []
                });
            }

            await new Promise(resolve => setTimeout(resolve, 3000));
        }

        setOrders(fetchedOrders);
    };

    const toggleOrderExpansion = (orderNumber) => {
        const newExpandedOrders = new Set(expandedOrders);
        if (newExpandedOrders.has(orderNumber)) {
            newExpandedOrders.delete(orderNumber);
        } else {
            newExpandedOrders.add(orderNumber);
        }
        setExpandedOrders(newExpandedOrders);
    };

    const clearFormAndTable = () => {
        setOrderNumbers("");
        setOrders([]);
        setFetchCount(0);
        setExpandedOrders(new Set());
    };

    // Function to export data as CSV
    const exportToCSV = () => {
        if (orders.length === 0) {
            alert("No data to export!");
            return;
        }

        // Define CSV headers - updated to include SKU and Weight
        let csvContent = "Order Number,ShipStation Order #,Status,Carrier,Externally Fulfilled,Serviced By,Tags,SKU,Item Name,Quantity,Weight,Item Fulfillment Status\n";

        // Loop through each order and its items
        orders.forEach(order => {
            const tags = order.tagIds.join(", ");

            if (order.items && order.items.length > 0) {
                order.items.forEach(item => {
                    const fulfillmentStatus = (item.quantity || 0) > 0 ? "Fulfilled" : "Unfulfilled";
                    const weight = item.weight ? `${item.weight.value} ${item.weight.units}` : "N/A";
                    const sku = item.sku || "N/A";

                    csvContent += `"${order.orderNumber}","${order.shipStationOrderNumber}","${order.orderStatus}","${order.carrierCode}","${order.externallyFulfilled ? "Yes" : "No"}","${order.servicedBy}","${tags}","${sku}","${item.name || "N/A"}","${item.quantity || 0}","${weight}","${fulfillmentStatus}"\n`;
                });
            } else {
                // In case no items
                const fulfillmentStatus = "Unfulfilled";
                csvContent += `"${order.orderNumber}","${order.shipStationOrderNumber}","${order.orderStatus}","${order.carrierCode}","${order.externallyFulfilled ? "Yes" : "No"}","${order.servicedBy}","${tags}","N/A","N/A","0","N/A","${fulfillmentStatus}"\n`;
            }
        });

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const date = new Date().toISOString().slice(0, 10);

        link.setAttribute("href", url);
        link.setAttribute("download", `shipstation_orders_${date}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h3 className="text-center mb-4">Check Shipstation Order Status</h3>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="orderNumbers" className="form-label">Enter Order Numbers (one per line)</label>
                                    <textarea
                                        id="orderNumbers"
                                        className="form-control"
                                        placeholder="Order Numbers (separated by newlines) - Leading '0' will be added automatically"
                                        value={orderNumbers}
                                        onChange={handleOrderNumbersChange}
                                        rows="6"
                                        required
                                    />
                                    <div className="form-text">
                                        Note: A leading "0" will be automatically added to each order number.
                                    </div>
                                </div>
                                <div className="d-flex justify-content-around">
                                    <button type="submit" className="btn btn-success">
                                        Show Details
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={clearFormAndTable}>
                                        Clear Form
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={exportToCSV} disabled={orders.length === 0}>
                                        Export to CSV
                                    </button>
                                </div>
                                <div className="fetchCount mt-3">
                                    <h4 style={{ color: "#6e18b4" }}>No. of Order Data Fetched: {fetchCount}</h4>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table for order details */}
            <div className="table-responsive detailOutput mt-4">
                <p>Fetched Order Details from the Shipstation</p>
                <table className="table table-responsive table-striped table-bordered">
                    <thead>
                        <tr>
                            <th>S.no</th>
                            <th>Store Order #</th>
                            <th>Shipstation Order #</th>
                            <th>Order Status</th>
                            {/* <th>Tracking Number</th> */}
                            <th>Shipped By</th>
                            <th>Externally Fulfilled</th>
                            <th>Serviced By</th>
                            <th>Tag Id's</th>
                            <th>SKU</th>
                            <th>Items & Quantity</th>
                            <th>Fulfillment Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="12" className="text-center">
                                    <div className="text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    Loading data...
                                </td>
                            </tr>
                        ) : orders.length === 0 ? (
                            <tr>
                                <td colSpan="12" className="text-center">No data available</td>
                            </tr>
                        ) : (
                            orders.map((order, index) => (
                                <React.Fragment key={index}>
                                    <tr>
                                        <td>{index + 1}</td>
                                        <td>{order.orderNumber}</td>
                                        <td>{order.shipStationOrderNumber}</td>
                                        <td className={
                                            order.orderStatus === "shipped" ? "status-shipped" :
                                                order.orderStatus === "cancelled" ? "status-cancelled" :
                                                    order.orderStatus === "on_hold" ? "status-onhold" :
                                                        "status-default"
                                        }>
                                            {order.orderStatus}
                                        </td>
                                        {/* <td>{order.trackingNumber}</td> */}
                                        <td>{order.carrierCode}</td>
                                        <td className={order.externallyFulfilled ? "text-success" : "text-secondary"}>
                                            {order.externallyFulfilled ? "Yes" : "No"}
                                        </td>
                                        <td>{order.servicedBy}</td>
                                        <td>{order.tagIds && order.tagIds.length > 0 ? order.tagIds.join(", ") : "N/A"}</td>
                                        <td>
                                            {order.items && order.items.length > 0 ? (
                                                <ul className="list-unstyled mb-0">
                                                    {order.items.slice(0, 2).map((item, i) => (
                                                        <li key={i}>
                                                            {item.sku || "N/A"}
                                                        </li>
                                                    ))}
                                                    {order.items.length > 2 && !expandedOrders.has(order.orderNumber) && (
                                                        <li>... and {order.items.length - 2} more</li>
                                                    )}
                                                    {expandedOrders.has(order.orderNumber) && order.items.slice(2).map((item, i) => (
                                                        <li key={i + 2}>
                                                            {item.sku || "N/A"}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "No items"
                                            )}
                                        </td>
                                        <td>
                                            {order.items && order.items.length > 0 ? (
                                                <ul className="list-unstyled mb-0">
                                                    {order.items.slice(0, 2).map((item, i) => (
                                                        <li key={i}>
                                                            {item.name || "N/A"} - Qty: {item.quantity || 0}
                                                        </li>
                                                    ))}
                                                    {order.items.length > 2 && !expandedOrders.has(order.orderNumber) && (
                                                        <li>... and {order.items.length - 2} more</li>
                                                    )}
                                                    {expandedOrders.has(order.orderNumber) && order.items.slice(2).map((item, i) => (
                                                        <li key={i + 2}>
                                                            {item.name || "N/A"} - Qty: {item.quantity || 0}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "No items"
                                            )}
                                        </td>
                                        <td>
                                            {order.items && order.items.length > 0 ? (
                                                order.items.map((item, i) => (
                                                    <div key={i} className={item.quantity > 0 ? "text-success" : "text-danger"}>
                                                        {item.quantity > 0 ? "Fulfilled" : "Unfulfilled"}
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="text-danger">Unfulfilled</span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-info btn-sm"
                                                onClick={() => toggleOrderExpansion(order.orderNumber)}
                                            >
                                                {expandedOrders.has(order.orderNumber) ? "Collapse" : "Expand"}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedOrders.has(order.orderNumber) && (
                                        <tr className="expanded-details">
                                            <td colSpan="12">
                                                <div className="p-3 bg-light">
                                                    <h5>Detailed Items for Order: {order.orderNumber}</h5>
                                                    <table className="table table-sm table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th>SKU</th>
                                                                <th>Product Name</th>
                                                                <th>Quantity</th>
                                                                <th>Unit Price</th>
                                                                <th>Total Price</th>
                                                                <th>Weight</th>
                                                                <th>Fulfillment Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {order.items.map((item, idx) => {
                                                                const fulfillmentStatus = (item.quantity || 0) > 0 ? "Fulfilled" : "Unfulfilled";
                                                                return (
                                                                    <tr key={idx}>
                                                                        <td>{item.sku || "N/A"}</td>
                                                                        <td>{item.name || "N/A"}</td>
                                                                        <td>{item.quantity || 0}</td>
                                                                        <td>${(item.unitPrice || 0).toFixed(2)}</td>
                                                                        <td>${((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}</td>
                                                                        <td>{item.weight ? `${item.weight.value} ${item.weight.units}` : "N/A"}</td>
                                                                        <td className={fulfillmentStatus === "Fulfilled" ? "text-success" : "text-danger"}>
                                                                            {fulfillmentStatus}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr>
                                                                <td colSpan="2" className="text-end"><strong>Order Total:</strong></td>
                                                                <td><strong>{order.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</strong></td>
                                                                <td colSpan="2">
                                                                    <strong>
                                                                        ${order.items.reduce((sum, item) => sum + ((item.unitPrice || 0) * (item.quantity || 0)), 0).toFixed(2)}
                                                                    </strong>
                                                                </td>
                                                                <td>
                                                                    <strong>
                                                                        {order.items.reduce((sum, item) => sum + (item.weight ? item.weight.value * item.quantity : 0), 0).toFixed(2)} ounces
                                                                    </strong>
                                                                </td>
                                                                <td></td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .status-shipped {
                    color: #28a745;
                    font-weight: bold;
                }
                .status-cancelled {
                    color: #dc3545;
                    font-weight: bold;
                }
                .status-onhold {
                    color: #ffc107;
                    font-weight: bold;
                }
                .status-default {
                    color: #6c757d;
                }
                .card {
                    border: none;
                    border-radius: 10px;
                }
                .btn-success {
                    background-color: #28a745;
                    border-color: #28a745;
                }
                .fetchCount h4 {
                    font-weight: bold;
                }
                .table th {
                    background-color: #6e18b4;
                    color: white;
                }
                .expanded-details {
                    background-color: #f8f9fa;
                }
            `}</style>
        </div>
    );
}

export default FetchOrderStatus;
